import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { X, Plus } from 'lucide-react';
import { getSystems, createSystem, deleteSystem, searchTrailAreas, getAreaTrails, importAreaTrails, getRecommendations, addRecommendedSystem } from '../api';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmModal';

function AddSystemModal({ onClose, onAdded }) {
  const toast = useToast();
  const [tab, setTab] = useState('search'); // 'search' | 'manual'
  const [query, setQuery] = useState('');
  const [areas, setAreas] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedArea, setSelectedArea] = useState(null);
  const [trails, setTrails] = useState([]);
  const [loadingTrails, setLoadingTrails] = useState(false);
  const [importing, setImporting] = useState(false);
  const [manualForm, setManualForm] = useState({ name: '', region: '', country: '' });
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);

  const handleQueryChange = useCallback((value) => {
    setQuery(value);
    setSelectedArea(null);
    setTrails([]);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!value.trim() || value.trim().length < 3) {
      setAreas([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await searchTrailAreas(value);
        setAreas(res.data || []);
      } catch {
        setAreas([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  }, []);

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  async function handleSelectArea(area) {
    setSelectedArea(area);
    setLoadingTrails(true);
    try {
      const [south, north, west, east] = area.boundingbox;
      const res = await getAreaTrails({ south, west, north, east });
      setTrails(res.data || []);
    } catch {
      toast.error('Failed to fetch trails for this area.');
    } finally {
      setLoadingTrails(false);
    }
  }

  async function handleImport() {
    if (!selectedArea) return;
    setImporting(true);
    try {
      const [south, north, west, east] = selectedArea.boundingbox;
      await importAreaTrails({
        name: selectedArea.name,
        south, west, north, east,
        lat: selectedArea.lat,
        lng: selectedArea.lng,
      });
      onAdded();
      onClose();
    } catch {
      toast.error('Import failed.');
    } finally {
      setImporting(false);
    }
  }

  async function handleManualAdd(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await createSystem({ ...manualForm, trails: [] });
      onAdded();
      onClose();
    } catch {
      toast.error('Failed to create system.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal__overlay" onClick={onClose}>
      <div className="modal__content" onClick={e => e.stopPropagation()}>
        <h2>ADD TRAIL SYSTEM</h2>

        <div className="tab-toggle">
          {['search', 'manual'].map(t => (
            <button key={t} onClick={() => { setTab(t); setSelectedArea(null); setTrails([]); }}
              className={`tab-toggle__btn${tab === t ? ' tab-toggle__btn--active' : ''}`}>
              {t === 'search' ? 'SEARCH TRAILS' : 'ADD MANUALLY'}
            </button>
          ))}
        </div>

        {tab === 'search' && !selectedArea && (
          <>
            <div className="search-input" style={{ marginBottom: '16px' }}>
              <input
                value={query}
                onChange={e => handleQueryChange(e.target.value)}
                placeholder="Search area (e.g. Whistler, Moab, Pisgah)..."
                autoFocus
              />
              {searching && (
                <div className="search-input__spinner">
                  <span className="spinner" style={{ width: 16, height: 16 }} />
                </div>
              )}

              {areas.length > 0 && (
                <div className="dropdown">
                  {areas.map(a => (
                    <button
                      key={`${a.osmType}-${a.id}`}
                      onClick={() => handleSelectArea(a)}
                      className="dropdown__item"
                    >
                      <div className="dropdown__item-row">
                        <span className="dropdown__item-name">{a.name}</span>
                        {['park', 'nature_reserve', 'protected_area', 'national_park', 'forest', 'wood', 'recreation_ground', 'state_park'].includes(a.type) && (
                          <span className="pill pill--type">
                            {a.type.replace(/_/g, ' ')}
                          </span>
                        )}
                        {a.userCount > 0 && (
                          <span className="pill pill--riders">
                            {a.userCount} rider{a.userCount !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      <div className="dropdown__item-sub">{a.fullName}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {areas.length === 0 && query.length >= 3 && !searching && (
              <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '20px' }}>No results found.</p>
            )}

            <button className="btn btn--secondary btn--full" onClick={onClose} style={{ marginTop: '12px' }}>Cancel</button>
          </>
        )}

        {tab === 'search' && selectedArea && (
          <>
            <button onClick={() => { setSelectedArea(null); setTrails([]); }} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '13px', cursor: 'pointer', marginBottom: '12px' }}>
              ← Back to results
            </button>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '20px' }}>{selectedArea.name}</div>
            </div>

            {loadingTrails ? (
              <div className="loading loading--sm"><span className="spinner" /></div>
            ) : trails.length > 0 ? (
              <>
                <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '12px' }}>
                  {trails.length} trail{trails.length !== 1 ? 's' : ''} found
                </div>
                <div style={{ maxHeight: '240px', overflowY: 'auto', display: 'grid', gap: '6px', marginBottom: '16px' }}>
                  {trails.map((t, i) => (
                    <div key={i} className="trail-preview">
                      <span className="trail-preview__name">{t.name}</span>
                      <div className="trail-preview__meta">
                        {t.length && <span className="trail-preview__length">{t.length} km</span>}
                        <span className={`badge badge--${t.difficulty}`}>{t.difficulty}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <button className="btn btn--primary btn--full" onClick={handleImport} disabled={importing}>
                  {importing ? <span className="spinner" /> : `IMPORT ${trails.length} TRAILS`}
                </button>
              </>
            ) : (
              <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '20px' }}>
                No tagged MTB trails found in this area. Try a more specific search or add trails manually.
              </p>
            )}

            <button className="btn btn--secondary btn--full" onClick={onClose} style={{ marginTop: '8px' }}>Cancel</button>
          </>
        )}

        {tab === 'manual' && (
          <form onSubmit={handleManualAdd}>
            <div className="form__group">
              <label>System Name *</label>
              <input value={manualForm.name} onChange={e => setManualForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Dupont State Forest" required />
            </div>
            <div className="form__row">
              <div className="form__group">
                <label>Region</label>
                <input value={manualForm.region} onChange={e => setManualForm(p => ({ ...p, region: e.target.value }))} placeholder="e.g. Western NC" />
              </div>
              <div className="form__group">
                <label>Country</label>
                <input value={manualForm.country} onChange={e => setManualForm(p => ({ ...p, country: e.target.value }))} placeholder="e.g. USA" />
              </div>
            </div>
            <div className="form__actions">
              <button type="button" className="btn btn--secondary flex-1" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn--primary flex-1" disabled={saving}>
                {saving ? <span className="spinner" /> : 'CREATE'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default function SystemsList() {
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [addingRec, setAddingRec] = useState(null);
  const toast = useToast();
  const confirm = useConfirm();
  const prevMatchingRef = useRef(new Set());

  const loadRef = useRef();
  loadRef.current = () => {
    getSystems()
      .then(res => setSystems(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const load = useCallback(() => loadRef.current(), []);

  useEffect(load, [load]);

  useEffect(() => {
    getRecommendations()
      .then(res => setRecommendations(res.data))
      .catch(() => {});
  }, [systems.length]);

  // Poll while any system is still matching
  useEffect(() => {
    const matching = systems.filter(s => s.matchingStatus === 'matching');
    if (matching.length === 0) {
      // Check if any systems just finished matching
      if (prevMatchingRef.current.size > 0) {
        for (const id of prevMatchingRef.current) {
          const system = systems.find(s => s._id === id);
          if (system && system.matchingStatus === 'complete' && system.matchedCount > 0) {
            toast.success(`Matched ${system.matchedCount} trail${system.matchedCount !== 1 ? 's' : ''} from Strava for ${system.name}`);
            const sTrails = system.trails || [];
            const sTotal = sTrails.length;
            if (sTotal > 0) {
              const sPct = Math.round((sTrails.filter(t => t.completed).length / sTotal) * 100);
              if (sPct === 100) {
                toast.success(`You've completed every trail in ${system.name}!`);
              } else if (sPct >= 75) {
                toast.success(`${sPct}% of ${system.name} complete — almost there!`);
              } else if (sPct >= 50) {
                toast.success(`${sPct}% of ${system.name} complete — keep going!`);
              } else if (sPct >= 25) {
                toast.success(`${sPct}% of ${system.name} complete!`);
              }
            }
          }
        }
        prevMatchingRef.current = new Set();
      }
      return;
    }

    prevMatchingRef.current = new Set(matching.map(s => s._id));
    const interval = setInterval(load, 3000);
    return () => clearInterval(interval);
  }, [systems, load, toast]);

  async function handleAddRecommendation(rec) {
    setAddingRec(rec._id);
    try {
      await addRecommendedSystem(rec._id);
      setRecommendations(prev => prev.filter(r => r._id !== rec._id));
      load();
    } catch {
      toast.error('Failed to add system.');
    } finally {
      setAddingRec(null);
    }
  }

  async function handleDelete(id) {
    const ok = await confirm('Remove this trail system from your list?', { confirmLabel: 'Remove' });
    if (!ok) return;
    try {
      await deleteSystem(id);
      load();
    } catch {
      toast.error('Failed to delete system. Please try again.');
    }
  }

  return (
    <div className="fade-in">
      <div className="page-header__row" style={{ marginBottom: '28px' }}>
        <div>
          <h1 className="page-header__title">TRAIL SYSTEMS</h1>
          <p className="page-header__subtitle">{systems.length} system{systems.length !== 1 ? 's' : ''} tracked</p>
        </div>
        <button className="btn btn--primary" onClick={() => setShowModal(true)}>+ ADD SYSTEM</button>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner spinner--lg" /></div>
      ) : systems.length === 0 ? (
        <div className="empty-state">
          <p>No trail systems yet. Add your first one!</p>
          <button className="btn btn--primary" style={{ marginTop: 16 }} onClick={() => setShowModal(true)}>Add System</button>
        </div>
      ) : (
        <div className="grid grid--gap-lg">
          {systems.map(system => {
            const trails = system.trails || [];
            const total = trails.length;
            const done = trails.filter(t => t.completed).length;
            const pct = total > 0 ? Math.round((done / total) * 100) : 0;
            const totalKm = trails.reduce((sum, t) => sum + (t.length || 0), 0);
            const doneKm = trails.filter(t => t.completed).reduce((sum, t) => sum + (t.length || 0), 0);
            return (
              <div key={system._id} className="card system-card">
                <Link to={`/systems/${system._id}`} className="flex-1">
                  <div className="system-card__name">{system.name}</div>
                  <div className="system-card__meta">
                    {[system.region, system.country].filter(Boolean).join(', ')}
                  </div>
                  <div className="system-card__progress-row">
                    <div className="progress-bar" style={{ flex: 1 }}>
                      <div className="progress-bar__fill" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="system-card__stats">
                      {done}/{total} trails · {Math.round(doneKm)}/{Math.round(totalKm)} km
                    </span>
                  </div>
                  {system.matchingStatus === 'matching' && (
                    <div className="matching-banner">
                      <span className="spinner spinner--sm" style={{ borderColor: 'var(--info-border)', borderTopColor: 'var(--info-text)' }} />
                      <span className="flex-1">Matching trails with your Strava rides...</span>
                      {system.matchedCount > 0 && (
                        <span style={{ fontWeight: 600 }}>
                          {system.matchedCount} matched
                        </span>
                      )}
                    </div>
                  )}
                </Link>
                <button className="btn btn--danger btn--sm" aria-label={`Remove ${system.name}`} onClick={() => handleDelete(system._id)}><X size={14} /></button>
              </div>
            );
          })}
        </div>
      )}

      {recommendations.length > 0 && (
        <div style={{ marginTop: '40px' }}>
          <h2 className="section-header__title" style={{ marginBottom: '4px' }}>RECOMMENDED FOR YOU</h2>
          <p className="text-muted" style={{ fontSize: '13px', marginBottom: '16px' }}>Based on where you ride on Strava</p>
          <div className="grid grid--gap-md">
            {recommendations.map(rec => (
              <div key={rec._id} className="card explore-card">
                <div className="flex-1">
                  <div className="explore-card__name">{rec.name}</div>
                  <div className="explore-card__meta">
                    {rec.trailCount} trail{rec.trailCount !== 1 ? 's' : ''} · {rec.distance} km away · {rec.nearbyRides} ride{rec.nearbyRides !== 1 ? 's' : ''} nearby
                  </div>
                </div>
                <button
                  className="btn btn--primary btn--sm"
                  onClick={() => handleAddRecommendation(rec)}
                  disabled={addingRec === rec._id}
                >
                  {addingRec === rec._id ? <span className="spinner spinner--sm" /> : <><Plus size={14} /> ADD</>}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {showModal && <AddSystemModal onClose={() => setShowModal(false)} onAdded={load} />}
    </div>
  );
}
